import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Package, Search, FileText, Truck, Factory, RefreshCw, XSquare, AlertTriangle } from 'lucide-react';
import IndentCreationDialog from '@/components/IndentCreationDialog';
import { getCategoryColor, CATEGORIES } from '@/lib/categories';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category colors - uses centralized getCategoryColor for consistency
const categoryColors = {
  FNG: 'bg-green-100 text-green-800 border-green-200',
  RMT: 'bg-blue-100 text-blue-800 border-blue-200',
  SFG: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  CTM: 'bg-amber-100 text-amber-800 border-amber-200',
  ASSET: 'bg-orange-100 text-orange-800 border-orange-200'
};

// Normalize category to uppercase code
const normalizeCategory = (cat) => {
  if (!cat) return 'RMT';
  const upper = cat.toUpperCase();
  // Map legacy values to new codes
  const mapping = { 'FINISHED_GOODS': 'FNG', 'RAW_MATERIAL': 'RMT', 'SEMI_FINISHED': 'SFG' };
  return mapping[upper] || upper;
};

const sourceTypeIcons = {
  MP: <Truck className="w-3 h-3" />,
  IP: <Factory className="w-3 h-3" />
};

export default function CreateIndentPage() {
  const { user, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryTab, setCategoryTab] = useState('production'); // 'production' (FNG/SFG/RMT) or 'ctm'
  const [sourceFilter, setSourceFilter] = useState('all'); // MP, IP, or all
  
  // Indent selection state
  const [selectedForIndent, setSelectedForIndent] = useState({});
  const [indentQuantities, setIndentQuantities] = useState({});
  const [openIndent, setOpenIndent] = useState(false);
  const [showIPWarning, setShowIPWarning] = useState(false);
  const [pendingIPItems, setPendingIPItems] = useState([]);
  
  // My Created Indents state
  const [myIndents, setMyIndents] = useState([]);
  const [loadingMyIndents, setLoadingMyIndents] = useState(false);
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'my_indents'

  // Fetch items on mount
  useEffect(() => {
    fetchItems();
  }, []);

  // Filter items when filters change
  useEffect(() => {
    filterItems();
  }, [items, searchTerm, categoryTab, sourceFilter]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/items`, { headers: getAuthHeader() });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load items');
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyIndents = async () => {
    setLoadingMyIndents(true);
    try {
      const response = await axios.get(`${API_URL}/indents/my-indents`, { headers: getAuthHeader() });
      setMyIndents(response.data || []);
    } catch (error) {
      console.error('Error fetching my indents:', error);
      toast.error('Failed to load your indents');
    } finally {
      setLoadingMyIndents(false);
    }
  };

  // Fetch my indents when switching to that tab
  useEffect(() => {
    if (activeTab === 'my_indents') {
      fetchMyIndents();
    }
  }, [activeTab]);

  const filterItems = () => {
    let filtered = [...items];

    // Filter by category tab - use 'category' field (source of truth)
    if (categoryTab === 'production') {
      // FNG/SFG/RMT items
      filtered = filtered.filter(item => {
        const cat = item.category?.toUpperCase();
        return cat === 'FNG' || cat === 'SFG' || cat === 'RMT';
      });
    } else {
      // CTM items
      filtered = filtered.filter(item => {
        const cat = item.category?.toUpperCase();
        return cat === 'CTM';
      });
    }

    // Filter by source type (MP/IP)
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(item => item.source_type === sourceFilter);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(search) ||
        item.item_code?.toLowerCase().includes(search) ||
        item.description?.toLowerCase().includes(search)
      );
    }

    // Sort alphabetically by name
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    setFilteredItems(filtered);
  };

  // Indent selection handlers
  const toggleIndentSelection = (item) => {
    // Use item.nett_status directly from backend (no frontend calculation)
    const nettStatus = item.nett_status || 0;
    const defaultQty = nettStatus < 0 ? Math.abs(nettStatus) : (item.reorder_quantity || item.min_stock_level || 1);
    
    setSelectedForIndent(prev => {
      if (prev[item.id]) {
        const { [item.id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [item.id]: item };
    });
    
    if (!indentQuantities[item.id]) {
      setIndentQuantities(prev => ({ ...prev, [item.id]: defaultQty }));
    }
  };

  const handleOpenIndentDialog = () => {
    const selectedItems = Object.values(selectedForIndent);
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    // Check if any selected items are IP (In-house Production) items
    const ipItems = selectedItems.filter(item => item.source_type === 'IP');
    if (ipItems.length > 0) {
      // Show warning dialog for IP items
      setPendingIPItems(ipItems);
      setShowIPWarning(true);
      return;
    }

    // No IP items, open dialog directly
    setOpenIndent(true);
  };

  const handleConfirmIPWarning = () => {
    // User confirmed - proceed to open indent dialog
    setShowIPWarning(false);
    setPendingIPItems([]);
    setOpenIndent(true);
  };

  const handleCancelIPWarning = () => {
    setShowIPWarning(false);
    setPendingIPItems([]);
  };

  const handleIndentSuccess = () => {
    setSelectedForIndent({});
    setIndentQuantities({});
    fetchItems();
  };

  const clearSelection = () => {
    setSelectedForIndent({});
  };

  const selectedCount = Object.keys(selectedForIndent).length;

  // Prepare items for the unified dialog
  // Use item.nett_status directly from backend (no frontend calculation)
  const getItemsForDialog = () => {
    return Object.values(selectedForIndent).map(item => {
      const nettStatus = item.nett_status || 0;
      return {
        item_id: item.id,
        item_name: item.name,
        item_code: item.item_code,
        unit: item.unit || 'NOS',
        source_type: item.source_type || 'MP',
        lead_time: item.lead_time || 0,
        nett_status: nettStatus,
        location_code: item.default_location_code || '',
        location_id: item.default_location_id || '',
        default_qty: indentQuantities[item.id] || Math.max(1, Math.abs(nettStatus))
      };
    });
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="create-indent-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-6 h-6 text-orange-600" />
              Create Indent
            </h1>
            <p className="text-muted-foreground">Select items to create purchase requisitions</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchItems} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Main Tabs: Create Indent vs My Indents */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="create" data-testid="tab-create-indent">
              <Package className="w-4 h-4 mr-2" />
              Create New Indent
            </TabsTrigger>
            <TabsTrigger value="my_indents" data-testid="tab-my-indents">
              <FileText className="w-4 h-4 mr-2" />
              My Indents ({myIndents.length})
            </TabsTrigger>
          </TabsList>

          {/* Create Indent Tab */}
          <TabsContent value="create" className="space-y-4">
            {/* Filters Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Filter Items</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category Tabs */}
                <Tabs value={categoryTab} onValueChange={setCategoryTab}>
                  <TabsList>
                    <TabsTrigger value="production" data-testid="tab-production">
                      <Package className="w-4 h-4 mr-2" />
                      FNG / SFG / RMT
                    </TabsTrigger>
                    <TabsTrigger value="ctm" data-testid="tab-ctm">
                      <Package className="w-4 h-4 mr-2" />
                      CTM
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Search and Source Filter */}
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[250px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by item name or code..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-input"
                      />
                    </div>
                  </div>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="source-filter">
                      <SelectValue placeholder="Source Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="MP">MP (Market Purchase)</SelectItem>
                      <SelectItem value="IP">IP (In-house Production)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Selection Actions */}
                <div className="flex items-center gap-4 pt-2 border-t">
                  <Button variant="outline" size="sm" onClick={clearSelection} disabled={selectedCount === 0}>
                    <XSquare className="w-4 h-4 mr-2" />
                    Clear Selection
                  </Button>
                  {selectedCount > 0 && (
                    <Badge variant="secondary" className="ml-auto">
                      {selectedCount} item(s) selected
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Items Table */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Items ({filteredItems.length})</CardTitle>
                    <CardDescription>
                      {categoryTab === 'production' ? 'Production items (FNG/SFG/RMT)' : 'Consumables, Tools & Miscellaneous'}
                    </CardDescription>
                  </div>
                  {selectedCount > 0 && (
                    <Button 
                      onClick={handleOpenIndentDialog} 
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="create-indent-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Create Indent ({selectedCount})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-muted-foreground">Loading items...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No items found matching your criteria</p>
                    <p className="text-sm mt-1">Try adjusting your filters or search term</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Item Name</TableHead>
                          <TableHead className="text-center">Category</TableHead>
                          <TableHead className="text-center">Source</TableHead>
                          <TableHead className="text-center">Location</TableHead>
                          <TableHead className="text-center">UoM</TableHead>
                          <TableHead className="text-right">Stock</TableHead>
                          <TableHead className="text-right">Nett Status</TableHead>
                          <TableHead className="text-right">Min Stock</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredItems.map((item) => {
                          // Use item.nett_status directly from backend (no frontend calculation)
                          const nettStatus = item.nett_status || 0;
                          const isSelected = !!selectedForIndent[item.id];
                          return (
                            <TableRow 
                              key={item.id} 
                              className={`cursor-pointer ${isSelected ? 'bg-orange-50' : ''}`}
                              onClick={() => toggleIndentSelection(item)}
                              data-testid={`item-row-${item.id}`}
                            >
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleIndentSelection(item)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className={categoryColors[normalizeCategory(item.category)] || ''}>
                                  {normalizeCategory(item.category)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {sourceTypeIcons[item.source_type || 'MP']}
                                  <span className="text-sm">{item.source_type || 'MP'}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {item.default_location_code ? (
                                  <Badge variant="outline" className="text-xs">{item.default_location_code}</Badge>
                                ) : (
                                  <span className="text-muted-foreground text-xs">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">{item.unit || '-'}</TableCell>
                              <TableCell className="text-right">{(item.stock_in_hand || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right">
                                <span className={nettStatus < 0 ? 'text-red-600 font-semibold' : 'text-green-600'}>
                                  {nettStatus.toFixed(2)}
                                </span>
                              </TableCell>
                              <TableCell className="text-right">{item.min_stock_level || 0}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Indents Tab */}
          <TabsContent value="my_indents" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">My Created Indents</CardTitle>
                    <CardDescription>
                      View indents you have created
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchMyIndents} disabled={loadingMyIndents}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingMyIndents ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loadingMyIndents ? (
                  <div className="p-8 text-center text-muted-foreground">Loading your indents...</div>
                ) : myIndents.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>You haven't created any indents yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Indent ID</TableHead>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-center">Location</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Linked SO</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {myIndents.map(indent => (
                          <TableRow key={indent.id}>
                            <TableCell className="font-mono text-sm">
                              {indent.indent_number || indent.id?.slice(0, 8)}
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{indent.item_name || indent.name}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              {indent.location_code ? (
                                <Badge variant="outline" className="text-xs">{indent.location_code}</Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{indent.quantity_required}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={indent.status === 'pending' ? 'secondary' : 
                                        indent.status === 'po_created' ? 'default' :
                                        indent.status === 'rejected' ? 'destructive' : 'outline'}
                              >
                                {indent.status?.replace(/_/g, ' ').toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {indent.created_at ? new Date(indent.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              }) : '-'}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {indent.so_number || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Unified Indent Creation Dialog */}
        <IndentCreationDialog
          open={openIndent}
          onOpenChange={setOpenIndent}
          items={getItemsForDialog()}
          onSuccess={handleIndentSuccess}
          title="Create Indents"
          description="Create purchase requisitions for selected items. Optionally link to a Sales Order for tracking."
          desiredPoDateRequired={false}
          sourceContext="manual"
        />

        {/* IP Items Warning Dialog */}
        <Dialog open={showIPWarning} onOpenChange={setShowIPWarning}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                In-house Production Items Selected
              </DialogTitle>
              <DialogDescription>
                You have selected {pendingIPItems.length} IP (In-house Production) item(s) for indent creation.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> IP items are normally manufactured in-house. Creating an indent means you want to <strong>purchase</strong> these items from a supplier instead.
                </p>
              </div>
              <div className="text-sm text-muted-foreground mb-2">Selected IP items:</div>
              <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                {pendingIPItems.map(item => (
                  <li key={item.id} className="flex items-center gap-2">
                    <Factory className="w-3 h-3 text-amber-600" />
                    <span>{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handleCancelIPWarning}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmIPWarning}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Yes, Create Indent for Purchase
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
